import { useEffect, useRef, useState } from 'react'
import { languageOptions, type Translator } from '../i18n/i18n'
import type { Language } from '../types'

interface LanguageSelectorProps {
  language: Language
  t: Translator
  onChange: (language: Language) => void
}

export function LanguageSelector({ language, t, onChange }: LanguageSelectorProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const selected = languageOptions.find((option) => option.code === language) ?? languageOptions[1]

  useEffect(() => {
    if (!open) return
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsidePress)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePress)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  return (
    <div className="language-picker" ref={containerRef}>
      <button
        className="language-button"
        type="button"
        aria-label={t('languageSelector')}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <bdi>{selected.shortLabel}</bdi><span className="language-chevron" aria-hidden="true">⌄</span>
      </button>
      {open && (
        <div className="language-menu" role="listbox" aria-label={t('languageSelector')}>
          {languageOptions.map((option) => (
            <button
              className="language-option"
              type="button"
              role="option"
              aria-selected={option.code === language}
              key={option.code}
              onClick={() => {
                setOpen(false)
                if (option.code !== language) onChange(option.code)
              }}
            >
              <bdi>{option.label}</bdi>
              {option.code === language && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
